/**
 * OfflineBanner — distinguishes three states the user might be in:
 *
 *   1. Online + reachable  → no banner (the optimistic happy path)
 *   2. Online + unreachable → amber: "We can't reach RouteFlow's servers"
 *                              (captive portal / transient backend outage)
 *   3. Offline             → berry: "You're offline"
 *
 * Strategy: rely on `useReachability` which combines navigator.onLine with
 * a periodic HEAD probe of the Supabase REST root. The probe distinguishes
 * captive-portal "lying online" from real connectivity, so the banner can
 * say something useful in each case.
 *
 * On the recovery edge (offline → reachable), we toast a "back online"
 * confirmation and invalidate every TanStack Query so the visible data
 * refreshes itself. `refetchOnReconnect: true` is already set globally in
 * App.tsx, but invalidation gives pages that paused their queries
 * (`enabled: false`, retry exhaustion) a clean nudge to retry.
 */
import { useEffect, useRef } from "react";
import { CloudOff, AlertTriangle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useReachability } from "@/hooks/useReachability";
import { cn } from "@/lib/utils";

export function OfflineBanner({ className }: { className?: string }) {
  const { online, reachable } = useReachability();
  const queryClient = useQueryClient();

  // Track edge: previously degraded → now healthy.
  const wasDegradedRef = useRef(false);
  useEffect(() => {
    const isDegraded = !online || !reachable;
    if (wasDegradedRef.current && !isDegraded) {
      // Recovered. Refetch the world.
      void queryClient.invalidateQueries();
      toast.success("Back online", { description: "Refreshed the page data." });
    }
    wasDegradedRef.current = isDegraded;
  }, [online, reachable, queryClient]);

  if (online && reachable) return null;

  const isOffline = !online;
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-start gap-2 border-b px-4 py-2 text-[12.5px]",
        isOffline
          ? "border-berry/30 bg-berry/10 text-ink-2"
          : "border-amber-rf/30 bg-amber-rf/10 text-ink-2",
        className,
      )}
    >
      {isOffline ? (
        <CloudOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-berry" aria-hidden="true" />
      ) : (
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-rf" aria-hidden="true" />
      )}
      <span>
        {isOffline ? (
          <>
            <span className="font-medium text-ink">You're offline.</span>{" "}
            Data on screen may be stale; new actions will fail until you reconnect.
          </>
        ) : (
          <>
            <span className="font-medium text-ink">Can't reach RouteFlow's servers.</span>{" "}
            Your connection looks online, but our backend isn't responding. We'll keep checking.
          </>
        )}
      </span>
    </div>
  );
}
